import React, { useState, useEffect, useRef } from 'react';
import { Select, Toast, Button } from '@douyinfe/semi-ui';
import { IconRefresh, IconVideo } from '@douyinfe/semi-icons';
import request from '../../utils/request';

const LessonSelector = ({ value, onChange, placeholder, style, disabled }) => {
    const [loading, setLoading] = useState(false);
    const [lessonList, setLessonList] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [internalValue, setInternalValue] = useState(null);
    const fetchedUids = useRef(new Set());

    useEffect(() => {
        if (value) {
            setInternalValue(value);
            // 如果当前列表中没有选中的课程，且之前没有尝试获取过该课程，则获取课程详情
            if (!lessonList.some(lesson => lesson.uid === value) && !fetchedUids.current.has(value)) {
                fetchLessonDetails([value]);
            }
        } else {
            setInternalValue(null);
        }
    }, [value]);

    const fetchLessonDetails = async (uids) => {
        try {
            // 标记这些UID已经尝试获取过
            uids.forEach(uid => fetchedUids.current.add(uid));

            const response = await request.get('/go/sp/lesson/listmini_lesson/', {
                params: {
                    uids: uids.join(',')
                }
            });
            if (response.errno === 0 && response.list) {
                setLessonList(prevList => {
                    const newList = [...prevList];
                    response.list.forEach(lesson => {
                        if (!newList.some(existingLesson => existingLesson.uid === lesson.uid)) {
                            newList.push(lesson);
                        }
                    });
                    return newList;
                });
            } else {
                // 如果没有找到对应的课程，也要更新状态以避免重复请求
                console.warn('未找到指定的课程或请求失败');
            }
        } catch (error) {
            console.error('获取课程详情失败:', error);
        }
    };

    const fetchLessonList = async () => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/lesson/listmini_lesson/', {
                params: {
                    page: 0,
                    limit: 200,
                    status: -1,
                    key: searchKey
                }
            });
            if (response.errno === 0) {
                setLessonList(response.list || []);
                // 重置已获取的UID集合，因为我们刚刚获取了完整的列表
                fetchedUids.current.clear();
            } else {
                Toast.error(response.errstr || '获取课程列表失败');
            }
        } catch (error) {
            console.error('获取课程列表失败:', error);
            Toast.error('获取课程列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLessonList();
    }, []);

    const handleChange = (selectedUid) => {
        setInternalValue(selectedUid);
        // 如果选择了null或空值，确保清除内部状态
        if (!selectedUid) {
            fetchedUids.current.clear();
        }
        onChange?.(selectedUid);
    };

    const renderCustomOption = (lesson) => {
        const optionStyle = {
            display: 'flex',
            paddingLeft: 24,
            paddingTop: 10,
            paddingBottom: 10,
        };
        return (
            <Select.Option
                key={lesson.uid}
                value={lesson.uid}
                style={optionStyle}
                showTick={true}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ fontSize: 14 }}>{lesson.name}</div>
                </div>
            </Select.Option>
        );
    };

    const renderSelectedItem = (optionNode) => {
        const selectedLesson = lessonList.find(lesson => lesson.uid === optionNode.value);
        if (!selectedLesson) return optionNode.value;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{selectedLesson.name}</span>
            </div>
        );
    };

    return (
        <Select
            loading={loading}
            value={internalValue}
            onChange={handleChange}
            style={{ ...style, maxWidth: 150 }}
            placeholder={placeholder || '请选择课程'}
            filter
            showClear
            filterOption={(input, option) => {
                const name = option?.label?.toString() || '';
                return name.toLowerCase().includes(input.toLowerCase());
            }}
            prefix={<IconVideo style={{ color: 'var(--semi-color-primary)' }} />}
            disabled={disabled}
            renderSelectedItem={renderSelectedItem}
            onDropdownVisibleChange={(visible) => {
                if (visible) {
                    setSearchKey('');
                    fetchLessonList();
                }
            }}
            onSearch={(value) => {
                setSearchKey(value);
                // 延迟300ms执行搜索，避免频繁请求
                setTimeout(() => {
                    fetchLessonList();
                }, 300);
            }}
            outerTopSlot={
                <Button
                    icon={<IconRefresh />}
                    type="tertiary"
                    onClick={fetchLessonList}
                    style={{ width: '100%', marginBottom: '8px' }}
                >
                    刷新列表
                </Button>
            }
        >
            {lessonList.map(lesson => renderCustomOption(lesson))}
        </Select>
    );
};

export default LessonSelector;